/*
Copyright© Celligence International, LLC - 2026

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained
access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useCallback, useState } from 'react';
import axiosCall from '../../services/axios';
import useAbortController from '../../common/hooks/useAbortController';
import { useAuthContext } from '../../contexts/AuthContext';

export type BorrowerLoginAudit = {
  loginAuditID: string;
  webUserID: string;
  email: string;
  eventDateTime: number;
  eventType: string;
  eventTypeKeyword: string;
  eventTypeDescription: string;
  eventResult: string;
  eventResultKeyword: string;
  eventResultDescription: string;
  failureReason: string;
  failureReasonKeyword: string;
  failureReasonDescription: string;
  IPAddress: string;
  IPVersion: string;
  browser: string;
  browserKeyword: string;
  browserDescription: string;
  clientOS: string;
  clientOSDescription: string;
  clientOSKeyword: string;
  sourceApp: string;
  sourceAppKeyword: string;
  sourceAppDescription: string;
};

export type SearchType = 'webUserID' | 'email' | 'sourceApp';

const useGetBorrowerLoginAudit = () => {
  const [loginAuditRecords, setLoginAuditRecords] = useState<BorrowerLoginAudit[]>([]);

  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const { token } = useAuthContext();
  const { get, reset } = useAbortController();

  const clearLoginAuditRecords = useCallback(() => {
    setLoginAuditRecords([]);
  }, []);

  const getBorrowerLoginAudit = useCallback(
    async (searchType: SearchType, searchValue: string, eventDate: string) => {
      if (!token || !searchValue || !eventDate) {
        return;
      }

      setLoading(true);
      setErrorMessage('');

      const controller = get();
      const config = {
        signal: controller.signal,
      };

      const headers = {
        'Session-Token': token,
        'Content-Type': 'application/json',
      };

      try {
        let url = '';

        switch (searchType) {
          case 'webUserID':
            url = process.env.REACT_APP_BORROWER_LOGIN_AUDIT_USER_DATE.replace('?', searchValue).replace(
              '?',
              eventDate,
            );
            break;
          case 'email':
            url = process.env.REACT_APP_BORROWER_LOGIN_AUDIT_EMAIL_DATE.replace('?', searchValue).replace(
              '?',
              eventDate,
            );
            break;
          case 'sourceApp':
            url = process.env.REACT_APP_BORROWER_LOGIN_AUDIT_SOURCE_APP_DATE.replace('?', searchValue).replace(
              '?',
              eventDate,
            );
            break;
        }

        const response = await axiosCall(
          {
            url,
            method: 'get',
          },
          config,
          headers,
        );

        setLoginAuditRecords(
          [...response].sort((firstRecord: BorrowerLoginAudit, secondRecord: BorrowerLoginAudit) => {
            return secondRecord.eventDateTime - firstRecord.eventDateTime;
          }),
        );
      } catch (error: any) {
        if (error.code === 'ERR_CANCELED') {
          return;
        }

        setLoginAuditRecords([]);

        if (error.response?.status === 404) {
          setErrorMessage('No audit records found. Try a different filter.');
        } else {
          setErrorMessage('Error loading borrower login audit records.');
          console.error(error);
        }
      } finally {
        setLoading(false);
      }
    },
    [get, token],
  );

  return {
    loginAuditRecords,
    errorMessage,
    loading,
    getBorrowerLoginAudit,
    clearLoginAuditRecords,
    reset,
  };
};

export default useGetBorrowerLoginAudit;
